import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Stack,
  Container,
  Typography,
  Button,
  Box,
  Tooltip,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Avatar,
  Chip,
  Switch,
  TextField
} from "@mui/material";
import {
  Edit,
  Delete,
  Add,
  ArrowBack,
  Call,
  Schedule,
  LocationOn,
  Warning,
  Person
} from "@mui/icons-material";
import Page from "../components/Page";
import {
  fetchCitySupportList,
  deleteCitySupportProfile,
  toggleCitySupportProfileStatus,
  fetchCityEdDetails,
  saveCityEdDetails
} from "src/utils/common";
import { notification } from "src/utils/messages";
import CustomLoader from "src/commonComponents/CustomLoader";
import { Routes } from "../constant/api_url";

const avatarBg = [
  "#dbeafe", "#dcfce7", "#fef3c7", "#fce7f3", "#e0e7ff", "#ede9fe", "#ffe4e6", "#cffafe"
];

const avatarFg = [
  "#1d4ed8", "#166534", "#92400e", "#be185d", "#3730a3", "#7c3aed", "#dc2626", "#0e7490"
];

export default function CitySupportList() {
  const navigate = useNavigate();
  const { cityId } = useParams();
  let loginUser = JSON.parse(localStorage.getItem("userData"));
  const isCityAdmin = loginUser?.u_type == 1;
  const activeCityId = isCityAdmin ? loginUser.city_id : (parseInt(cityId) || 0);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: "" });

  const [edDetails, setEdDetails] = useState(null);
  const [edDialog, setEdDialog] = useState(false);
  const [edForm, setEdForm] = useState({ name: "", mobile: "" });
  const [edProfileImgFile, setEdProfileImgFile] = useState(null);
  const [edProfileImgPreview, setEdProfileImgPreview] = useState("");
  const [savingEd, setSavingEd] = useState(false);

  const cityName = list[0]?.city_name || "";

  const getList = useCallback(async () => {
    setLoading(true);
    const resp = await fetchCitySupportList(activeCityId);
    if (resp) setList(resp);
    setLoading(false);
  }, [activeCityId]);

  useEffect(() => {
    getList();
  }, [getList]);

  const getEdDetails = useCallback(async () => {
    const resp = await fetchCityEdDetails(activeCityId);
    if (resp) setEdDetails(resp);
  }, [activeCityId]);

  useEffect(() => {
    getEdDetails();
  }, [getEdDetails]);

  const handleOpenEdDialog = () => {
    setEdForm({
      name: edDetails?.ed_name || "",
      mobile: edDetails?.ed_mobile || ""
    });
    setEdProfileImgFile(null);
    if (edDetails?.ed_profile_img) {
      setEdProfileImgPreview(`${Routes.API_HOSTNAME}city-ed-details/${edDetails.ed_profile_img}`);
    } else {
      setEdProfileImgPreview("");
    }
    setEdDialog(true);
  };

  const handleSaveEd = async () => {
    if (!edForm.name.trim()) {
      notification({ type: "error", message: "Please enter the Executive Director name" })();
      return;
    }
    if (!edForm.mobile.trim()) {
      notification({ type: "error", message: "Please enter the mobile number" })();
      return;
    }
    if (!/^\d{10}$/.test(edForm.mobile.trim())) {
      notification({ type: "error", message: "Enter exactly 10 digits" })();
      return;
    }
    setSavingEd(true);
    const data = new FormData();
    data.append("city_id", activeCityId);
    data.append("ed_name", edForm.name.trim());
    data.append("ed_mobile", edForm.mobile.trim());
    if (edDetails?.city_ed_id) data.append("city_ed_id", edDetails.city_ed_id);
    if (edProfileImgFile) {
      data.append("ed_profile_img", edProfileImgFile);
    }
    const resp = await saveCityEdDetails(data);
    let notify = notification({
      type: resp?.status === 1 ? "success" : "error",
      message: resp?.message || "Something went wrong"
    });
    notify();
    setSavingEd(false);
    if (resp?.status === 1) {
      setEdDialog(false);
      getEdDetails();
    }
  };

  const handleToggleStatus = async (row) => {
    const data = new FormData();
    data.append("id", row.id);
    const resp = await toggleCitySupportProfileStatus(data);
    let notify = notification({
      type: resp?.status === 1 ? "success" : "error",
      message: resp?.message || "Something went wrong"
    });
    notify();
    if (resp?.status === 1) getList();
  };

  const handleDelete = async () => {
    const data = new FormData();
    data.append("id", deleteDialog.id);
    const resp = await deleteCitySupportProfile(data);
    let notify = notification({
      type: resp?.status === 1 ? "success" : "error",
      message: resp?.message || "Something went wrong"
    });
    notify();
    setDeleteDialog({ open: false, id: null, name: "" });
    if (resp?.status === 1) getList();
  };

  const getInitial = (name) => name?.charAt(0)?.toUpperCase() || "?";

  const getAvatarColors = (name) => {
    const index = (name?.length || 0) % avatarBg.length;
    return { bg: avatarBg[index], fg: avatarFg[index] };
  };

  const getHours = (row) => {
    if (row.weekday_hours) return row.weekday_hours;
    return `${row.weekday_hours_from || "10:00"} - ${row.weekday_hours_to || "18:00"}`;
  };

  return (
    <Page title="City Support | LVB Privilege">
      <Container maxWidth="xl">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={4}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            {!isCityAdmin && cityId && (
              <IconButton
                size="small"
                onClick={() => navigate("/city-support")}
                sx={{ mt: 0.5, borderRadius: 2, border: 1, borderColor: "grey.300" }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
            )}
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, fontSize: 24, letterSpacing: "-0.02em" }}>
                {isCityAdmin ? "City Support" : cityName ? `City Support - ${cityName}` : "City Support"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {isCityAdmin ? "Manage helpline profiles for your city" : `Manage helpline profiles for ${cityName || "the city"}`}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              if (!isCityAdmin && cityId) {
                navigate("/city-support/add", { state: { preselectedCityId: parseInt(cityId), preselectedCityName: cityName } });
              } else {
                navigate("/city-support/add");
              }
            }}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, px: 3, py: 1.25 }}
          >
            Add Support Profile
          </Button>
        </Stack>

        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            border: 1,
            borderColor: "grey.200",
            boxShadow: "0 4px 20px -2px rgba(0,0,0,0.05)",
            mb: 4
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                src={edDetails?.ed_profile_img ? `${Routes.API_HOSTNAME}city-ed-details/${edDetails.ed_profile_img}` : undefined}
                sx={{ bgcolor: "#fef3c7", color: "#92400e", width: 44, height: 44 }}
              >
                <Person />
              </Avatar>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                  Executive Director
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {edDetails
                    ? `${edDetails.ed_name} — ${edDetails.ed_mobile}`
                    : "No Executive Director assigned"}
                </Typography>
              </Box>
            </Stack>
            <Button
              variant={edDetails ? "outlined" : "contained"}
              size="small"
              startIcon={edDetails ? <Edit /> : <Add />}
              onClick={handleOpenEdDialog}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              {edDetails ? "Edit" : "Add"}
            </Button>
          </Stack>
        </Card>

        {loading ? (
          <CustomLoader />
        ) : list.length === 0 ? (
          <Card sx={{ p: 6, textAlign: "center", borderRadius: 3, boxShadow: "0 4px 20px -2px rgba(0,0,0,0.05)" }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No City Support Details Available
            </Typography>
            <Typography variant="body2" color="text.disabled" mb={2}>
              Add a support profile to get started
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => navigate("/city-support/add", { state: { preselectedCityId: parseInt(cityId), preselectedCityName: cityName } })}>
              Add Support Profile
            </Button>
          </Card>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
              gap: 3
            }}
          >
            {list.map((item) => {
              const { bg, fg } = getAvatarColors(item.name);
              return (
                <Card
                  key={item.id}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: 1,
                    borderColor: "grey.200",
                    boxShadow: "0 4px 20px -2px rgba(0,0,0,0.05)",
                    overflow: "hidden",
                    transition: "all 0.2s",
                    "&:hover": { borderColor: "primary.light" }
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        src={`${Routes.API_HOSTNAME}city-support/${item.profile_image}` || undefined}
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: bg,
                          color: fg,
                          fontWeight: 700,
                          fontSize: 18
                        }}
                      >
                        {getInitial(item.name)}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {item.designation}
                        </Typography>
                        {item.support_title && (
                          <Typography variant="caption" sx={{ display: "block", color: "primary.main", fontWeight: 600, lineHeight: 1.3, mt: 0.25 }}>
                            {item.support_title}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    <Chip
                      label={item.is_active == 1 ? "Active" : "Inactive"}
                      size="small"
                      sx={{
                        borderRadius: "9999px",
                        fontWeight: 600,
                        fontSize: 10,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        height: 20,
                        bgcolor: item.is_active == 1 ? "#dcfce7" : "#f3f4f6",
                        color: item.is_active == 1 ? "#166534" : "#6b7280"
                      }}
                    />
                  </Stack>

                  <Stack spacing={1.5} mb={2.5}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Call sx={{ fontSize: 18, color: "#94a3b8" }} />
                      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                        {item.mobile}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Schedule sx={{ fontSize: 18, color: "#94a3b8" }} />
                      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                        {getHours(item)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <LocationOn sx={{ fontSize: 18, color: "#94a3b8", mt: 0.25 }} />
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "text.secondary",
                          lineHeight: 1.4,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden"
                        }}
                      >
                        {item.address}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Box
                    sx={{
                      pt: 2,
                      borderTop: 1,
                      borderColor: "grey.200",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between"
                    }}
                  >
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          sx={{
                            borderRadius: 1.5,
                            color: "#94a3b8",
                            "&:hover": { color: "primary.main", bgcolor: "grey.50" }
                          }}
                          onClick={() => navigate(`/city-support/edit/${item.id}`, { state: { preselectedCityId: activeCityId } })}
                        >
                          <Edit sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          sx={{
                            borderRadius: 1.5,
                            color: "#94a3b8",
                            "&:hover": { color: "#dc2626", bgcolor: "#fee2e2" }
                          }}
                          onClick={() =>
                            setDeleteDialog({ open: true, id: item.id, name: item.name })
                          }
                        >
                          <Delete sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.05em" }}>
                        {item.is_active == 1 ? "ACTIVE" : "INACTIVE"}
                      </Typography>
                      <Switch
                        size="small"
                        checked={item.is_active == 1}
                        onChange={() => handleToggleStatus(item)}
                        sx={{
                          "& .MuiSwitch-thumb": {
                            bgcolor: "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                          },
                          "& .MuiSwitch-track": {
                            bgcolor: item.is_active == 1 ? "#004867" : "#e2e8f0"
                          },
                          "&.Mui-checked": {
                            "& + .MuiSwitch-track": {
                              bgcolor: "#004867"
                            }
                          }
                        }}
                      />
                    </Stack>
                  </Box>
                </Card>
              );
            })}
          </Box>
        )}

        <Dialog
          open={edDialog}
          onClose={() => setEdDialog(false)}
          PaperProps={{
            sx: { borderRadius: 3, boxShadow: "0 4px 20px -2px rgba(0,0,0,0.05)", maxWidth: 480, width: "100%" }
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, fontSize: 18, pb: 1 }}>
            {edDetails?.city_ed_id ? "Edit" : "Add"} Executive Director
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <TextField
                label="Full Name"
                value={edForm.name}
                onChange={(e) => setEdForm({ ...edForm, name: e.target.value })}
                fullWidth
                size="small"
              />
              <TextField
                label="Mobile Number"
                value={edForm.mobile}
                onChange={(e) => setEdForm({ ...edForm, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                fullWidth
                size="small"
                inputProps={{ inputMode: "numeric", pattern: "\\d{10}" }}
              />
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "text.secondary", fontSize: 13 }}>
                  Profile Image
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, py: 1.5, borderStyle: "dashed" }}
                >
                  {edProfileImgFile ? edProfileImgFile.name : "Choose Profile Image"}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setEdProfileImgFile(file);
                        setEdProfileImgPreview(URL.createObjectURL(file));
                      }
                    }}
                  />
                </Button>
                {edProfileImgPreview && (
                  <Box sx={{ mt: 1.5, textAlign: "center" }}>
                    <Avatar
                      src={edProfileImgPreview}
                      variant="rounded"
                      sx={{ width: 100, height: 100, mx: "auto", border: 1, borderColor: "grey.300" }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      Image Preview
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          </DialogContent>
          <Box sx={{ px: 3, pb: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              onClick={() => setEdDialog(false)}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveEd}
              disabled={savingEd}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              {savingEd ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Dialog>

        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, id: null, name: "" })}
          PaperProps={{
            sx: { borderRadius: 3, boxShadow: "0 4px 20px -2px rgba(0,0,0,0.05)", maxWidth: 440 }
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <Stack direction="row" spacing={2} sx={{ px: 3, pt: 3 }}>
              <Avatar
                sx={{ bgcolor: "#fee2e2", color: "#dc2626", width: 48, height: 48 }}
              >
                <Warning />
              </Avatar>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, fontSize: 18 }}>
                  Delete Support Profile
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
                  Are you sure you want to delete the{" "}
                  <strong>{deleteDialog.name}</strong> support profile? This will
                  stop showing this contact to members immediately.
                </Typography>
              </Box>
            </Stack>
            <Box
              sx={{
                bgcolor: "grey.100",
                px: 3,
                py: 2,
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3
              }}
            >
              <Button
                onClick={() => setDeleteDialog({ open: false, id: null, name: "" })}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
              >
                Delete
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      </Container>
    </Page>
  );
}
